import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

// Aura's own default `primary` semantic color is emerald (green) — see
// @primeuix/themes/dist/aura/base. This app's actual brand accent is cyan (see
// tailwind.config.ts's `primary` scale, already used throughout for Tailwind
// utility classes like `bg-primary-100`/`text-primary-700`). Left uncustomized,
// every PrimeNG "primary"-colored element (buttons, focus rings, checkboxes/radios,
// the datepicker's selected/today circle, tabs, badges, ...) renders green instead
// of matching the rest of the app.
//
// `{cyan.*}` references Aura's own bundled primitive cyan scale, which is byte-for-byte
// the same palette as Tailwind's default `cyan` — so this doesn't introduce a new color,
// it just points PrimeNG's primary token at the one the app already uses.
//
// Shared between app.config.ts and .storybook/preview.ts so they can't drift apart again
// the way bare `Aura` did.
export const GodAppPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '{cyan.50}',
      100: '{cyan.100}',
      200: '{cyan.200}',
      300: '{cyan.300}',
      400: '{cyan.400}',
      500: '{cyan.500}',
      600: '{cyan.600}',
      700: '{cyan.700}',
      800: '{cyan.800}',
      900: '{cyan.900}',
      950: '{cyan.950}',
    },
    colorScheme: {
      // Aura's own default action shade is {primary.500}; this app's legacy theme instead uses
      // 600 as the "--primary-color" default (see god-app-theme/_variables.scss), e.g. for the
      // active tab's text/underline color. Matching it here, not just the raw 50-950 scale
      // above, so shade-sensitive elements (active tab, focus rings, ...) land on the exact
      // same blue the legacy app used, not just the right hue.
      light: {
        primary: {
          color: '{primary.600}',
        },
      },
    },
  },
  components: {
    // The app lays out every dialog itself (create-dialog-layout: header, main, footer), as the
    // legacy theme expected with $dialogContentPadding: 0. Aura pads the content by default, and
    // its runtime styles are unlayered, so they beat the legacy rule in @layer primeng.
    dialog: {
      content: {
        padding: '0',
      },
    },
    // The context menus (app-menu-button, a p-menu) share the look of the popover menus, such as
    // "Nuovo": the legacy rule asks for rounded-xl, which Aura's unlayered radius would override
    menu: {
      root: {
        borderRadius: '0.75rem',
      },
    },
    // The section headers of the task forms, as the legacy theme drew them: a grey band with dark
    // text, darker on hover, open or not. Aura's white header and muted text left no contrast.
    // The form is the only accordion of the app.
    accordion: {
      panel: {
        borderColor: '#dee2e6',
      },
      header: {
        color: '{zinc.800}',
        hoverColor: '{zinc.800}',
        activeColor: '{zinc.800}',
        activeHoverColor: '{zinc.800}',
        background: '#f8f9fa',
        hoverBackground: '#e9ecef',
        activeBackground: '#f8f9fa',
        activeHoverBackground: '#e9ecef',
        padding: '1rem',
        fontWeight: '600',
        borderRadius: '0',
        toggleIcon: {
          color: '{zinc.800}',
          hoverColor: '{zinc.800}',
          activeColor: '{zinc.800}',
          activeHoverColor: '{zinc.800}',
        },
        first: {
          topBorderRadius: '0',
        },
        last: {
          bottomBorderRadius: '0',
          activeBottomBorderRadius: '0',
        },
      },
      // As the legacy form: a little room all around a section's content. Aura's own leaves none
      // above and a wide margin on both sides
      content: {
        padding: 'calc(1.5 * var(--spacing))',
      },
    },
  },
});
